import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { publicApi, resolveImageUrl } from "../lib/api";
import { Button } from "../components/ui/button";
import { ArrowRight, Calendar, Truck, Shield } from "lucide-react";

export default function RentPage() {
    const [machines, setMachines] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        publicApi.getRentalMachines().then(res => setMachines(res.data)).catch(console.error).finally(() => setLoading(false));
    }, []);

    return (
        <div className="min-h-[70vh]" data-testid="rent-page">
            <div className="bg-card border-b border-border relative overflow-hidden">
                {/* Background Video */}
                <div className="absolute inset-0">
                    <video
                        autoPlay
                        muted
                        loop
                        playsInline
                        className="absolute w-full h-full object-cover"
                        src={`${process.env.REACT_APP_BACKEND_URL}/api/files/dryice-marketplace/hero/background.mp4`}
                        poster="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg'/%3E"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-background via-background/85 to-background/30" />
                    <div className="absolute inset-0 bg-background/20" />
                </div>
                <div className="container mx-auto px-6 lg:px-12 py-12 lg:py-20 relative z-10">
                    <p className="font-mono text-xs uppercase tracking-widest text-primary mb-4">Equipment Rental</p>
                    <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black text-foreground tracking-tight mb-4">
                        Rent a Dry Ice Blaster
                    </h1>
                    <p className="text-muted-foreground text-base sm:text-lg max-w-2xl">
                        Professional dry ice blasting equipment available for rent. Free local pickup from our Apex, NC facility. Free delivery within 25 miles.
                    </p>
                </div>
            </div>

            <div className="container mx-auto px-6 lg:px-12 py-8">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
                    <div className="bg-card border border-border p-5 flex items-start gap-3">
                        <Calendar className="h-6 w-6 text-primary shrink-0 mt-1" />
                        <div>
                            <h3 className="font-heading font-bold text-foreground text-sm">Flexible Rental Periods</h3>
                            <p className="text-muted-foreground text-xs mt-1">Daily, weekly, and monthly rates. Auto-discounted at 4+ days.</p>
                        </div>
                    </div>
                    <div className="bg-card border border-border p-5 flex items-start gap-3">
                        <Truck className="h-6 w-6 text-primary shrink-0 mt-1" />
                        <div>
                            <h3 className="font-heading font-bold text-foreground text-sm">Free Local Pickup & Delivery</h3>
                            <p className="text-muted-foreground text-xs mt-1">Pickup from Apex, NC or free delivery within 25 miles.</p>
                        </div>
                    </div>
                    <div className="bg-card border border-border p-5 flex items-start gap-3">
                        <Shield className="h-6 w-6 text-primary shrink-0 mt-1" />
                        <div>
                            <h3 className="font-heading font-bold text-foreground text-sm">$100 Reservation Deposit</h3>
                            <p className="text-muted-foreground text-xs mt-1">Secure your dates with a $100 deposit. Balance due at pickup or online.</p>
                        </div>
                    </div>
                </div>

                {loading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[1, 2, 3].map(i => <div key={i} className="h-96 bg-card border border-border animate-pulse" />)}
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {machines.map(m => (
                            <Link key={m.id} to={`/rent/${m.id}`} className="group bg-card border border-border hover:border-primary/50 transition-all" data-testid={`rental-card-${m.id}`}>
                                <div className="aspect-[4/3] bg-muted/30 overflow-hidden flex items-center justify-center p-4">
                                    {m.images?.[0] ? (
                                        <img src={resolveImageUrl(m.images[0])} alt={m.model} className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300" />
                                    ) : (
                                        <div className="text-center">
                                            <p className="text-muted-foreground font-heading font-bold text-xl">{m.model}</p>
                                            <p className="text-muted-foreground text-sm">{m.year}</p>
                                        </div>
                                    )}
                                </div>
                                <div className="p-5 border-t border-border">
                                    <h3 className="font-heading text-lg font-bold text-foreground mb-1 group-hover:text-primary transition-colors">
                                        {m.model} ({m.year})
                                    </h3>
                                    <p className="text-muted-foreground text-xs mb-3 line-clamp-2">{m.description}</p>
                                    <p className="text-muted-foreground text-xs mb-3">Includes: {m.includes}</p>
                                    <div className="grid grid-cols-3 gap-2 text-center mb-4">
                                        <div className="bg-muted/30 p-2">
                                            <p className="text-primary font-bold text-sm">${m.daily_rate}</p>
                                            <p className="text-muted-foreground text-[10px] uppercase">Daily</p>
                                        </div>
                                        <div className="bg-muted/30 p-2">
                                            <p className="text-primary font-bold text-sm">${m.weekly_rate?.toLocaleString()}</p>
                                            <p className="text-muted-foreground text-[10px] uppercase">Weekly</p>
                                        </div>
                                        <div className="bg-muted/30 p-2">
                                            <p className="text-primary font-bold text-sm">${m.monthly_rate?.toLocaleString()}</p>
                                            <p className="text-muted-foreground text-[10px] uppercase">Monthly</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center text-primary text-sm font-mono uppercase tracking-wider">
                                        Book Now <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
